// ResumenComparativoHelper.js
// Compara arrays HD/EQUIP (resumen_facturacion, resumen_tasas, etc.) para
// ResumenComparativoHDEquip. Detalle largo: ver CHANGELOG_2026-08.md.

import { capitalizePalabras } from "./CapitalizePalabras";

const SUFIJO_HD = "_hd";
const SUFIJO_EQUIP_DEFAULT = "_facturacion";

// Conceptos sin contraparte "_hd" (solo EQUIP, ej. tasa/tasas).
const CONCEPTOS_SIN_COMPARACION = ["tasa", "tasas"];

// Etiquetas amigables por concepto; lo que no esté aquí usa formateo genérico.
const ETIQUETAS_CONOCIDAS = {
  documento: "Documento",
  idequip: "ID Cliente",
  vencimiento: "Vencimiento",
  folio: "Folio",
};

const formatearEtiqueta = (concepto) => {
  if (ETIQUETAS_CONOCIDAS[concepto] !== undefined) {
    return ETIQUETAS_CONOCIDAS[concepto];
  }
  return capitalizePalabras(concepto.replace(/_/g, " "));
};

// Normaliza para comparar (null/undefined -> "", trim, minúsculas).
const normalizar = (valor) => {
  if (valor === null || valor === undefined) return "";
  return String(valor).trim().toLowerCase();
};

const sonIguales = (valorHd, valorEquip) => {
  return normalizar(valorHd) === normalizar(valorEquip);
};

// Texto de "Observaciones" + si el concepto cuenta como correcto.
const calcularObservacion = (concepto, equip, coincide) => {
  if (concepto === "tasa") {
    return { observacion: "Registrada", esCorrecto: true };
  }

  if (concepto === "tasas") {
    const valorNumerico = Number(equip);
    const esCorrecto = !isNaN(valorNumerico) && valorNumerico > 0;
    return { observacion: esCorrecto ? "Correcto" : "Incompleto", esCorrecto };
  }

  if (coincide) {
    return { observacion: "Correcto", esCorrecto: true };
  }

  if (esVacio(equip)) {
    return { observacion: "No encontrado", esCorrecto: false };
  }

  return { observacion: `No coincide: ${equip}`, esCorrecto: false };
};

// true si el valor se considera sin información (null/undefined/""/0/"0").
export const esVacio = (valor) => {
  return (
    valor === null ||
    valor === undefined ||
    valor === "" ||
    valor === 0 ||
    valor === "0"
  );
};

// Texto de celda: el valor, o "No encontrado" si está vacío.
export const formatearValor = (valor) => {
  return esVacio(valor) ? "No encontrado" : valor;
};

// Nombres de concepto presentes en los registros (sin sufijos).
const obtenerConceptos = (registros, sufijoEquip) => {
  const conceptos = [];
  registros.forEach((registro) => {
    if (registro === undefined || registro === null) return;
    Object.keys(registro).forEach((llave) => {
      let concepto = null;
      if (llave.endsWith(SUFIJO_HD)) {
        concepto = llave.slice(0, -SUFIJO_HD.length);
      } else if (llave.endsWith(sufijoEquip)) {
        concepto = llave.slice(0, -sufijoEquip.length);
      }
      if (concepto !== null && !conceptos.includes(concepto)) {
        conceptos.push(concepto);
      }
    });
  });
  return conceptos;
};

// Construye los "grupos" (uno por registro) que consume ResumenComparativoHDEquip.
// conceptosExcluidos: conceptos a omitir por completo (ej. "idequip" cuando
// no aplica validar el ID Cliente en ese proceso).
export const construirResumenComparativo = (
  registros,
  sufijoEquip = SUFIJO_EQUIP_DEFAULT,
  conceptosExcluidos = []
) => {
  if (registros === undefined || registros === null || !Array.isArray(registros)) {
    return [];
  }

  const nombresConceptos = obtenerConceptos(registros, sufijoEquip).filter(
    (concepto) => !conceptosExcluidos.includes(concepto)
  );

  return registros.map((registro, indice) => {
    const conceptos = nombresConceptos.map((concepto) => {
      const hd = registro[`${concepto}${SUFIJO_HD}`];
      const equip = registro[`${concepto}${sufijoEquip}`];
      const coincide = sonIguales(hd, equip);
      const { observacion, esCorrecto } = calcularObservacion(concepto, equip, coincide);

      return {
        concepto,
        etiqueta: formatearEtiqueta(concepto),
        hd,
        equip,
        coincide,
        observacion,
        esCorrecto,
      };
    });

    // Identifica el registro (usa "documento" si existe).
    const documento = conceptos.find((item) => item.concepto === "documento");
    const identificador =
      documento !== undefined && (documento.hd || documento.equip)
        ? `Documento ${documento.hd || documento.equip}`
        : `Registro ${indice + 1}`;

    return {
      identificador,
      coincideTodo: conceptos.every((item) => item.esCorrecto),
      conceptos,
    };
  });
};

// Mensajes de "qué no cuadra" para la advertencia de MhusaFinalizacion.
export const obtenerInconsistencias = (grupos, etiquetaEquip = "EQUIP") => {
  if (grupos === undefined || grupos === null || !Array.isArray(grupos)) {
    return [];
  }

  const mensajes = [];

  grupos.forEach((grupo) => {
    grupo.conceptos.forEach((item) => {
      if (CONCEPTOS_SIN_COMPARACION.includes(item.concepto)) return;
      if (item.coincide) return;

      const faltaInformacion = esVacio(item.hd) || esVacio(item.equip);

      if (item.concepto === "documento" && faltaInformacion) {
        mensajes.push(
          `${grupo.identificador}: el documento no se encontró en ${etiquetaEquip}.`
        );
        return;
      }

      if (item.concepto === "idequip") {
        mensajes.push(
          `${grupo.identificador}: los ID de los clientes no coinciden.`
        );
        return;
      }

      if (item.concepto === "vencimiento") {
        mensajes.push(
          `${grupo.identificador}: las fechas de vencimiento no coinciden.`
        );
        return;
      }

      // Respaldo genérico (ej. folio u otro concepto sin regla propia).
      mensajes.push(
        faltaInformacion
          ? `${grupo.identificador}: ${item.etiqueta} no se encontró en ${etiquetaEquip}.`
          : `${grupo.identificador}: ${item.etiqueta} no coincide entre HD y ${etiquetaEquip}.`
      );
    });
  });

  return mensajes;
};
